/**
 * Hotel Currency Config API
 *
 * Fetches global currency/region/language configuration.
 * Called once in listing, stored in hotelConfigSlice for reuse.
 */
import type { HotelCurrencyConfigCBPayload } from '@contracts';
import { HOTELS_ENDPOINTS } from '@/api/endpoints';
import { logger } from '@/shared/utils/logger';
import {
  baseFetch,
  createApiError,
  buildCBUrlParamsLite,
  buildCBHeaders,
} from './booking-review/baseApi';

// ============================================
// Error Class
// ============================================

export const HotelCurrencyConfigError = createApiError('HotelCurrencyConfigError');

// ============================================
// Types
// ============================================

export interface CurrencyInfo {
  displayName: string;
  code: string;
  name: string;
  symbol: string;
}

export interface RegionCurrencies {
  popularTitle: string;
  otherTitle: string;
  popular: string[];
  other: string[];
}

export interface RegionInfo {
  id: string;
  name: string;
  lName: string;
  smallName: string;
  phoneCode: string;
  code: string;
  alpha3Code: string;
  entity: string;
  baseURL: string;
  defaultLng: string;
  defaultUserCurrency: string;
  languages: string[];
  currencies: RegionCurrencies;
  defaultCountry: { id: string; name: string };
  defaultCurry: CurrencyInfo;
}

export interface LanguageInfo {
  code: string;
  iso2Code: string;
  name: string;
  lname: string;
  symbol: string;
}

export interface CurrencyConfigData {
  regions: RegionInfo[];
  languages: Record<string, LanguageInfo>;
  currencies: Record<string, CurrencyInfo>;
}

export interface HotelCurrencyConfigRequestParams {
  readonly sessionId: string;
  readonly userId: string;
}

export interface HotelCurrencyConfigResponse {
  readonly type: 'hotels-currency-config';
  readonly success: boolean;
  readonly data: CurrencyConfigData | null;
  readonly error?: { code: string; message: string };
}

// ============================================
// Raw API Response Type
// ============================================

interface RawResponse {
  success?: boolean;
  data?: CurrencyConfigData;
  error?: { code: string; message: string };
}

// ============================================
// Helper Functions
// ============================================

/**
 * Build the CB backend payload with urlParams, headers, and requestBody
 * Uses common builders from baseApi (lite variant - no requestId/idContext)
 */
export function buildCBPayload(): HotelCurrencyConfigCBPayload {
  return {
    urlParams: buildCBUrlParamsLite(),
    headers: buildCBHeaders(),
    requestBody: {
      requestMap: {
        'global-config-web': {}, // Required by Python API
      },
    },
  };
}

// ============================================
// API Function
// ============================================

/**
 * Fetch currency config from the API
 * POST /hotels/currency-config?sessionId=...&userId=...
 */
export async function fetchHotelCurrencyConfig(
  params: HotelCurrencyConfigRequestParams,
): Promise<HotelCurrencyConfigResponse> {
  const payload = buildCBPayload();

  const { data, error } = await baseFetch<RawResponse>({
    endpoint: HOTELS_ENDPOINTS.CURRENCY_CONFIG,
    queryParams: { sessionId: params.sessionId, userId: params.userId },
    body: payload,
    logTag: 'HotelCurrencyConfigClient',
  });

  // Network/HTTP error
  if (error) {
    return { type: 'hotels-currency-config', success: false, data: null, error };
  }

  // API returned error
  if (data?.success === false && data?.error) {
    logger.warn('[HotelCurrencyConfigClient] API returned error:', data.error);
    return { type: 'hotels-currency-config', success: false, data: null, error: data.error };
  }

  // Success
  const configData = data?.data ?? null;
  logger.info('[HotelCurrencyConfigClient] Success:', {
    regions: configData?.regions?.length ?? 0,
    currencies: Object.keys(configData?.currencies ?? {}).length,
  });

  return { type: 'hotels-currency-config', success: true, data: configData };
}
